import {
  $createRangeSelection,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  RangeSelection,
  SKIP_DOM_SELECTION_TAG,
  SKIP_SCROLL_INTO_VIEW_TAG,
} from 'lexical';

import {isNil, isNotNil} from 'ramda';

import {TextEditorApi, TextEditorSelection} from '../../../types/TextEditorApi';
import {expandTextEditorSelectionToWords} from '../../../utils/expandTextEditorSelectionToWords';
import {normalizeTextEditorSelection} from '../../../utils/normalizeTextEditorSelection';
import {$collectEditorNodeRanges} from './collectEditorNodeRanges';
import {$getEditorIndexRange} from './getEditorIndexRange';
import {$resolveEditorIndexRange} from './resolveEditorIndexRange';
import {$serializeEditorHtml} from './serializeEditorHtml';
import {$serializeEditorRange} from './serializeEditorRange';

interface CapturedPoint {
  key: NodeKey;
  offset: number;
  type: 'text' | 'element';
}

interface CapturedSelection extends TextEditorSelection {
  anchor: CapturedPoint;
  focus: CapturedPoint;
  textContent: string;
}

const $isPointRestorable = (point: CapturedPoint) => {
  const node = $getNodeByKey(point.key);

  if (isNil(node) || !node.isAttached()) {
    return false;
  }

  if (point.type === 'text') {
    return $isTextNode(node) && point.offset <= node.getTextContentSize();
  }

  return $isElementNode(node) && point.offset <= node.getChildrenSize();
};

const $captureSelection = (selection: RangeSelection): CapturedSelection | null => {
  const range = $getEditorIndexRange(selection);

  if (isNil(range)) {
    return null;
  }

  return {
    ...range,
    anchor: {
      key: selection.anchor.key,
      offset: selection.anchor.offset,
      type: selection.anchor.type,
    },
    focus: {key: selection.focus.key, offset: selection.focus.offset, type: selection.focus.type},
    textContent: $getRoot().getTextContent(),
  };
};

const $captureNormalizedSelection = (selection: RangeSelection): CapturedSelection | null => {
  const range = $getEditorIndexRange(selection);

  if (isNil(range)) {
    return null;
  }

  const {leaves} = $collectEditorNodeRanges();
  // Block separators and decorators have no character of their own; both end a word.
  const charAt = (offset: number) => {
    const leaf = leaves.find((candidate) => candidate.start <= offset && offset < candidate.end);

    if (isNil(leaf) || !$isTextNode(leaf.node)) {
      return null;
    }

    return leaf.node.getTextContent().slice(offset - leaf.start, offset - leaf.start + 1) || null;
  };
  const trimmedRange = normalizeTextEditorSelection(range, (offset) => {
    const leaf = leaves.find((candidate) => candidate.start <= offset && offset < candidate.end);
    const character = leaf?.node
      .getTextContent()
      .slice(offset - leaf.start, offset - leaf.start + 1);

    return isNil(leaf) || character === '\n';
  });

  if (isNil(trimmedRange)) {
    return null;
  }

  const normalizedRange = expandTextEditorSelectionToWords(trimmedRange, charAt);

  const normalized =
    normalizedRange.length === range.length ? selection : $resolveEditorIndexRange(normalizedRange);

  return isNil(normalized) ? null : $captureSelection(normalized);
};

const $restoreCapturedRange = (target: CapturedSelection): RangeSelection | null => {
  if ($getRoot().getTextContent() !== target.textContent) {
    return null;
  }

  if (!$isPointRestorable(target.anchor) || !$isPointRestorable(target.focus)) {
    return null;
  }

  const range = $createRangeSelection();
  range.anchor.set(target.anchor.key, target.anchor.offset, target.anchor.type);
  range.focus.set(target.focus.key, target.focus.offset, target.focus.type);

  const indexRange = $getEditorIndexRange(range);
  const isSameRange =
    isNotNil(indexRange) &&
    indexRange.index === target.index &&
    indexRange.length === target.length;

  return isSameRange ? range : null;
};

export function createSuggestionInputEditorApi(
  editor: LexicalEditor,
  transformStringToNodes: (value: string) => LexicalNode[]
) {
  let captured: CapturedSelection | null = null;
  let isSelectionInvalidated = false;

  const removeUpdateListener = editor.registerUpdateListener(({editorState}) => {
    editorState.read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        isSelectionInvalidated = false;
        captured = selection.isCollapsed() ? null : $captureNormalizedSelection(selection);
        return;
      }

      // No selection is the editor losing focus: the range stays for the AI controls as long as
      // the state still holds it. Any other selection kind is a new choice by the employee.
      if (isNotNil(selection) || (isNotNil(captured) && isNil($restoreCapturedRange(captured)))) {
        captured = null;
      }
    });
  });

  const readDocument = () => editor.getEditorState().read(() => $serializeEditorHtml(editor));

  const isCaptured = (selection: TextEditorSelection, target: CapturedSelection) =>
    selection.length > 0 && target.index === selection.index && target.length === selection.length;

  const api: TextEditorApi = {
    getSelection: () => {
      const root = editor.getRootElement();
      const nativeSelection = root?.ownerDocument.getSelection();
      const nativeRange =
        isNotNil(nativeSelection) && nativeSelection.rangeCount > 0
          ? nativeSelection.getRangeAt(0)
          : null;

      // Native selectionchange can lag behind the toolbar focus handoff.
      if (
        !isSelectionInvalidated &&
        isNotNil(root) &&
        isNotNil(nativeRange) &&
        root.contains(nativeRange.commonAncestorContainer) &&
        (!nativeRange.collapsed || root.contains(root.ownerDocument.activeElement))
      ) {
        editor.getEditorState().read(
          () => {
            const selection = $createRangeSelection();
            selection.applyDOMRange(nativeRange);
            captured = selection.isCollapsed() ? null : $captureNormalizedSelection(selection);
          },
          {editor}
        );
      }

      return isNil(captured) ? null : {index: captured.index, length: captured.length};
    },
    getContent: (selection) => {
      if (isNil(selection)) {
        return readDocument();
      }

      // Selected HTML export clones Lexical nodes and requires a writable context.
      let content = '';
      editor.update(
        () => {
          content = $serializeEditorRange(editor, selection) ?? '';
        },
        {discrete: true, tag: [SKIP_DOM_SELECTION_TAG, SKIP_SCROLL_INTO_VIEW_TAG]}
      );
      return content;
    },
    replaceSelection: (selection, content) => {
      const target = captured;

      if (isNil(target) || !isCaptured(selection, target)) {
        return readDocument();
      }

      editor.update(
        () => {
          const range = $restoreCapturedRange(target);

          if (isNil(range)) {
            return;
          }

          $setSelection(range);
          const nodes = transformStringToNodes(content);

          if (nodes.length === 0) {
            range.removeText();
            return;
          }

          range.insertNodes(nodes);
        },
        {discrete: true}
      );

      captured = null;
      isSelectionInvalidated = true;

      return readDocument();
    },
    focus: () => {
      const target = captured;

      // Lexical answers a focus without a selection by collapsing to the end of the document, and
      // the update listener above would then drop the range the AI controls still refer to.
      // Putting the captured range back first lets the focus keep it.
      if (isNotNil(target)) {
        editor.update(
          () => {
            const range = $restoreCapturedRange(target);

            if (isNotNil(range)) {
              $setSelection(range);
            }
          },
          {discrete: true}
        );
      }

      editor.focus();
    },
  };

  return {
    api,
    dispose: removeUpdateListener,
    invalidateSelection: () => {
      captured = null;
      isSelectionInvalidated = true;
    },
  };
}
